import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { ModuleFeatureGuard } from 'app/core/auth/guards/module-feature.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { RoleGuard } from 'app/core/auth/guards/role.guard';
import { TabletEntryGuard } from 'app/core/auth/guards/tablet-entry.guard';
import { TabletModeRedirectGuard } from 'app/core/auth/guards/tablet-mode-redirect.guard';
import { TabletSessionGuard } from 'app/core/auth/guards/tablet-session.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { ROLES } from 'shared-models';

export const appRoutes: Route[] = [
  //redirectTo y canActivate no se pueden usar juntos así que he implementado este routing en dos pasos para roles complejos
  //como auditor o gestor
  // Step 1: First redirect to a route that will check authentication
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'auth-check',
  },

  {
    path: 'signed-in-redirect',
    pathMatch: 'full',
    redirectTo: 'auth-check',
  },

  // Step 2: This route checks auth and then applies role-based routing
  {
    path: 'auth-check',
    canActivate: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    children: [],
  },

  {
    path: '',
    canActivate: [TabletModeRedirectGuard, NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () => import('app/pages/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'sign-in',
        loadComponent: () => import('app/pages/auth/sign-in/sign-in.component').then((c) => c.AuthSignInComponent),
      },
      {
        path: 'reset-password/:id',
        loadComponent: () =>
          import('app/pages/auth/reset-password/reset-password.component').then((c) => c.AuthResetPasswordComponent),
      },
      {
        path: 'register-only-for-devs',
        loadComponent: () => import('app/pages/auth/register/register.component').then((c) => c.RegisterComponent),
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'tablet/sign-in',
        canActivate: [TabletEntryGuard],
        loadComponent: () =>
          import('app/pages/tablet/tablet-sign-in/tablet-sign-in.component').then((c) => c.TabletSignInComponent),
      },
      {
        path: 'tablet/clock',
        canActivate: [TabletSessionGuard],
        loadComponent: () =>
          import('app/pages/tablet/tablet-clock/tablet-clock.component').then((c) => c.TabletClockComponent),
      },
    ],
  },
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('app/pages/auth/sign-out/sign-out.routes') },
      {
        path: 'reset-password/:id',
        loadComponent: () =>
          import('app/pages/auth/reset-password/reset-password.component').then((c) => c.AuthResetPasswordComponent),
      },
    ],
  },
  {
    path: 'landing',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    data: {
      roles: [
        ROLES.SUPERADMIN,
        ROLES.ADMIN,
        ROLES.MANAGER,
        ROLES.USER,
        ROLES.GESTOR,
        ROLES.AUDITOR,
        ROLES.CONFIG_ADMIN,
      ],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: '', loadChildren: () => import('app/pages/landing/landing.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'profile',
        loadComponent: () => import('app/pages/profile/profile.component').then((c) => c.ProfileComponent),
      },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    /**
     * ⚠️ **Bloque aparte del de `time-tracking`, aunque la ruta lo parezca.** Existe para dar
     * acceso al ADMIN, que no está en los roles del otro, y es a donde apunta la tarjeta
     * «Turnos» del Admin Panel. Guardarlo solo allí dejaba esta puerta abierta justo al rol
     * que más la usa.
     */
    data: {
      moduleFeature: 'timeRecords',
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'time-tracking/automatizations',
        loadComponent: () =>
          import(
            'app/pages/time-tracking/pages/time-tracking-automatizations/time-tracking-automatizations.component'
          ).then((c) => c.TimeTrackingAutomatizationsComponent),
      },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    // Cubre `routine` y `validation` por `canActivateChild`. `automatizations` cuelga del
    // bloque de arriba, que lleva su propio guard.
    data: {
      moduleFeature: 'timeRecords',
      roles: [ROLES.SUPERADMIN, ROLES.MANAGER, ROLES.USER],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'time-tracking', loadChildren: () => import('app/pages/time-tracking/time-tracking.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    // El dashboard de empresa son KPIs de jornada: sin fichaje no tiene nada que enseñar.
    data: {
      moduleFeature: 'timeRecords',
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'enterprise-dashboard',
        loadComponent: () =>
          import('app/pages/landing/home/manager-dashboard/landing-manager-dashboard.component').then(
            (c) => c.LandingManagerDashboardComponent
          ),
      },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER],
      moduleFeature: 'projects',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: 'my-assignments', loadChildren: () => import('app/pages/my-assignments/my-assignments.routes') },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.GESTOR],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'admin', loadChildren: () => import('app/pages/admin/admin.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.MANAGER, ROLES.USER],
      moduleFeature: 'fileManagement',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'files', loadChildren: () => import('app/pages/file-manager-user/file-manager-user.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.MANAGER, ROLES.USER, ROLES.GESTOR],
      moduleFeature: 'applications',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: 'applications', loadChildren: () => import('app/pages/applications-user/applications-user.routes') },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.CONFIG_ADMIN],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'configuration', loadChildren: () => import('app/pages/configuration/configuration.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.CONFIG_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
      moduleFeature: 'planner',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'planner', loadChildren: () => import('app/pages/planner/planner.routes').then((m) => m.PLANNER_ROUTES) }],
  },
  {
    /**
     * La agenda del empleado, fuera de `/planner` a propósito.
     *
     * No puede colgar de esa ruta porque el bloque de arriba solo deja pasar a los roles
     * de gestión, y el router **no** prueba el siguiente bloque cuando un guard rechaza:
     * la ruta quedaría muerta para justo quien tiene que verla. Sigue detrás de
     * `ModuleFeatureGuard`, así que respeta el interruptor del planner.
     */
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.GESTOR],
      moduleFeature: 'planner',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'mis-turnos',
        loadComponent: () =>
          import('app/pages/planner/my-schedule/my-schedule.component').then((m) => m.MyScheduleComponent),
      },
    ],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    // Analíticas está construida entera sobre TimeRecordService.
    data: {
      moduleFeature: 'timeRecords',
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.GESTOR, ROLES.AUDITOR],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'analytics', loadChildren: () => import('app/pages/analytics/analytics.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard, ModuleFeatureGuard],
    canActivateChild: [AuthGuard, RoleGuard, ModuleFeatureGuard],
    component: LayoutComponent,
    // ⚠️ `reports` y no `timeRecords`: el informe de proyectos sobrevive sin fichaje. Ver `reportsEnabled`.
    data: {
      moduleFeature: 'reports',
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.GESTOR, ROLES.AUDITOR],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'reports', loadChildren: () => import('app/pages/reports/reports.routes') }],
  },
  {
    path: '',
    canActivate: [TabletModeRedirectGuard, AuthGuard, RoleGuard],
    canActivateChild: [AuthGuard, RoleGuard],
    component: LayoutComponent,
    data: {
      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.GESTOR, ROLES.AUDITOR],
    },
    resolve: {
      initialData: initialDataResolver,
    },
    children: [{ path: 'faq', loadChildren: () => import('app/pages/faq/faq.routes') }],
  },
];
